import React, { useState, useEffect, useContext } from 'react';
import {
    AppBar, Toolbar, Button, Box, IconButton, useMediaQuery, Drawer, List,
    ListItem, ListItemButton, ListItemText, Typography, Menu, Avatar,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import DesignServicesRoundedIcon from '@mui/icons-material/DesignServicesRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ContactsRoundedIcon from '@mui/icons-material/ContactsRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import BookingsIcon from '@mui/icons-material/EventNote';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import Logo from '../../images/logo/Logo.svg';
import User from '../../images/logo/User.svg';
import Fade from '@mui/material/Fade';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/authcontext';

interface NavItem {
    label: string;
    path: string;
    icon: JSX.Element;
}

const NavigationBar = () => {
    const isMobile = useMediaQuery('(max-width:860px)');
    const [mobileOpen, setMobileOpen] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const [guestAnchorEl, setGuestAnchorEl] = useState<any>(null);
    const location = useLocation();
    const navigate = useNavigate();
    const authContext = useContext(AuthContext);

    useEffect(() => {
        // (empty effect, or add logic if needed)
    }, [authContext?.authorities, location]);

    if (!authContext) throw new Error('NavigationBar must be used within an AuthProvider');

    const { isAuthenticated, user, authorities, logout, loading } = authContext;
    const userProfileId = authContext?.user?.id;

    if (loading) {
        return null;
    }

    const baseNavItems: NavItem[] = [
        { label: 'Home', path: '/', icon: <HomeRoundedIcon /> },
        { label: 'Works', path: '/works', icon: <WorkRoundedIcon /> },
        { label: 'Services', path: '/service', icon: <DesignServicesRoundedIcon /> },
        { label: 'About Us', path: '/about-us', icon: <InfoRoundedIcon /> },
    ];

    const providerNavItems: NavItem[] = [
        ...baseNavItems,
    ];

    const drawerNavItems = isAuthenticated ? [
        ...baseNavItems,
        { label: 'Bookings', path: '/booking', icon: <BookingsIcon /> },
        ...(authorities.includes('ROLE_PROVIDER') ? [
            { label: 'Add Contractor Details', path: '/details-add', icon: <BuildRoundedIcon /> },
            { label: 'Contractors Profile', path: '/contractors-profile', icon: <BusinessRoundedIcon /> }
        ] : [])
    ] : baseNavItems;

    const menuNavItems = isAuthenticated ? [
        { label: 'Bookings', path: '/booking', icon: <BookingsIcon /> },
        ...(authorities.includes('ROLE_PROVIDER') ? [
            { label: 'Add Details', path: '/details-add', icon: <BuildRoundedIcon /> },
            { label: 'Contractors Profile', path: '/contractors-profile', icon: <BusinessRoundedIcon /> } 
        ] : []),
        { label: 'Logout', path: '/logout', icon: <LogoutIcon /> }
    ] : [];

    const navItems = authorities.includes('ROLE_PROVIDER') ? providerNavItems : baseNavItems;

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleGuestMenuOpen = (event: React.MouseEvent<HTMLElement>) => setGuestAnchorEl(event.currentTarget);
    const handleGuestMenuClose = () => setGuestAnchorEl(null);

    const handleLogout = () => {
        logout();
        handleMenuClose();
        navigate('/');
    };

    const handleGuestMenuClick = (path: string) => {
        navigate(path);
        handleGuestMenuClose();
    };

    const handleNavClick = (path: string) => {
     
        if (path === '/logout') {
            handleLogout();
            return;
        }
        if (path === '/contractors-profile' && userProfileId) {
            navigate(path);
        } else {
            navigate(path);
        }
        if (isMobile) handleDrawerToggle();
        handleMenuClose();
    };

    const drawer = (
        <Box sx={{ display: 'flex', flexDirection: 'column', background: 'linear-gradient(to bottom, #FFFFFF, #F8F8F8)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, backgroundColor: '#FFFFFF', position: 'sticky', top: 0, zIndex: 1 }}>
                <Link to="/">
                    <img src={Logo} alt="logo" style={{ maxWidth: '150px' }} />
                </Link>
                <IconButton onClick={handleDrawerToggle} sx={{ color: '#89652A', backgroundColor: 'rgba(184, 134, 11, 0.08)' }}>
                    <CloseRoundedIcon />
                </IconButton>
            </Box>

            {isAuthenticated && user && (
                <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.12), rgba(184, 134, 11, 0.05))', mb: 2, borderRadius: '0 0 16px 16px' }}>
                    <Avatar sx={{ width: 72, height: 72, bgcolor: '#B8860B', mb: 2, fontSize: '30px', fontWeight: 'bold' }}>
                        {(user as { firstName?: string })?.firstName?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography sx={{ fontSize: '22px', textTransform: 'capitalize', fontFamily: "League Spartan", fontWeight: 700, color: '#B8860B', mb: 0.5 }}>
                        {(user as { firstName?: string })?.firstName}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', fontFamily: "League Spartan", fontWeight: 500, color: '#89652A', opacity: 0.8 }}>
                        Welcome back!
                    </Typography>
                </Box>
            )}

            <List sx={{ flex: 1, pt: 1, px: 2 }}>
                {drawerNavItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                        <ListItem key={item.label} disablePadding sx={{ mb: 1 }}>
                            <ListItemButton
                                onClick={() => handleNavClick(item.path)}
                                sx={{
                                    py: 1.5,
                                    px: 2,
                                    backgroundColor: isActive ? 'rgba(184, 134, 11, 0.15)' : 'white',
                                    borderRadius: '12px',
                                }}
                            >
                                <Box sx={{
                                    mr: 2,
                                    color: isActive ? '#89652A' : '#89652A',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 40,
                                    height: 40,
                                    borderRadius: '10px',
                                    backgroundColor: isActive ? 'rgba(184, 134, 11, 0.1)' : 'rgba(0, 0, 0, 0.03)'
                                }}>
                                    {item.icon}
                                </Box>
                                <ListItemText primary={item.label} sx={{ '& .MuiTypography-root': { fontSize: '18px', fontFamily: "League Spartan", fontWeight: 600, color: isActive ? '#B8860B' : '#333333' } }} />
                                {isActive && <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#B8860B', ml: 1 }} />}
                            </ListItemButton>
                        </ListItem>
                    );
                })}
                <ListItem disablePadding sx={{ mb: 1 }}>
                    <ListItemButton
                        onClick={() => handleNavClick('/contact-us')}
                        sx={{
                            py: 1.5,
                            px: 2,
                            backgroundColor: location.pathname === '/contact-us' ? 'rgba(184, 134, 11, 0.15)' : 'white',
                            borderRadius: '12px',
                        }}
                    >
                        <Box sx={{
                            mr: 2,
                            color: location.pathname === '/contact-us' ? '#89652A' : '#89652A',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: '40px',
                            borderRadius: '10px',
                            backgroundColor: location.pathname === '/contact-us' ? 'rgba(184, 134, 11, 0.1)' : 'rgba(0, 0, 0, 0.03)'
                        }}>
                            <ContactsRoundedIcon />
                        </Box>
                        <ListItemText primary="Contact Us" sx={{ '& .MuiTypography-root': { fontSize: '18px', fontFamily: "League Spartan", fontWeight: 600, color: location.pathname === '/contact-us' ? '#B8860B' : '#333333' } }} />
                        {location.pathname === '/contact-us' && <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#B8860B', ml: 1 }} />}
                    </ListItemButton>
                </ListItem>
            </List>

            <Box sx={{ mt: 'auto', p: 3, backgroundColor: '#FFFFFF' }}>
                {isAuthenticated ? (
                    <Button
                        fullWidth
                        onClick={handleLogout}
                        startIcon={<LogoutIcon />}
                        sx={{
                            justifyContent: 'center',
                            background: 'rgba(235, 87, 87, 0.05)',
                            color: 'red',
                            textTransform: 'none',
                            fontSize: '16px',
                            fontFamily: "League Spartan",
                            fontWeight: 600,
                            py: 1.8,
                            borderRadius: '12px',
                        }}
                    >
                        Logout
                    </Button>
                ) : (
                    <Button
                        fullWidth
                        component={Link}
                        to="/choose"
                        onClick={handleDrawerToggle}
                        sx={{
                            backgroundColor: '#89652A',
                            color: 'white',
                            textTransform: 'none',
                            fontSize: '18px',
                            fontFamily: "League Spartan",
                            fontWeight: 700,
                            py: 1.8,
                            borderRadius: '12px',
                        }}
                    >
                        Login / Register
                    </Button>
                )}
                {!isAuthenticated && (
                    <Typography sx={{ fontSize: '14px', fontFamily: "League Spartan", fontWeight: 500, color: '#757575', textAlign: 'center', mt: 2 }}>
                        Sign in to access all features
                    </Typography>
                )}
            </Box>
        </Box>
    );

    const userMenu = (
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            TransitionComponent={Fade}
            transitionDuration={300}
            PaperProps={{
                elevation: 0,
                sx: {
                    width: 300,
                    borderRadius: '16px',
                    mt: 1,
                    overflow: 'visible',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 20,
                        width: 10,
                        height: 10,
                        bgcolor: '#FFFFFF',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                    },
                },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
            <Box sx={{ position: 'relative' }}>
                <Box sx={{
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    borderBottom: '1px solid rgba(184, 134, 11, 0.1)',
                    position: 'relative'
                }}>
                    <Avatar
                        sx={{
                            width: 40,
                            height: 40,
                            bgcolor: '#B8860B',
                            fontSize: '22px',
                            fontWeight: 'bold',
                        }}
                    >
                        {(user as { firstName?: string })?.firstName?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        ml: 1.5
                    }}>
                        <Typography sx={{
                            fontSize: '16px',
                            fontFamily: "League Spartan",
                            fontWeight: 700,
                            color: '#89652A',
                            textTransform: 'capitalize',
                            lineHeight: 1.2
                        }}>
                            {(user as { firstName?: string })?.firstName}
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            mt: 0.2
                        }}>
                            <Typography sx={{
                                fontSize: '14px',
                                fontFamily: "League Spartan",
                                fontWeight: 600,
                                color: '#89652A',
                                opacity: 0.8
                            }}>
                                Welcome Back!
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <List sx={{ py: 1, px: 1 }}>
                    {menuNavItems.map((item, index) => {
                        const isActive = location.pathname === item.path;
                        const isLogout = item.path === '/logout';

                        return (
                            <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
                                <ListItemButton
                                    onClick={() => handleNavClick(item.path)}
                                    sx={{
                                        py: 0.75,
                                        px: 1.5,
                                        backgroundColor: isActive ? 'rgba(184, 134, 11, 0.08)' : 'transparent',
                                        borderRadius: '12px',
                                        boxShadow: 'none',
                                        '&:hover': {
                                            backgroundColor: isLogout ? 'rgba(235, 87, 87, 0.05)' : 'rgba(184, 134, 11, 0.05)',
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        mr: 1.5,
                                        color: isLogout ? '#EB5757' : '#89652A',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 36,
                                        height: 36,
                                        borderRadius: '10px',
                                        backgroundColor: isLogout ? 'rgba(235, 87, 87, 0.08)' : 'rgba(184, 134, 11, 0.08)'
                                    }}>
                                        {item.icon}
                                    </Box>
                                    <ListItemText
                                        primary={item.label}
                                        sx={{
                                            '& .MuiTypography-root': {
                                                fontSize: '16px',
                                                fontFamily: "League Spartan",
                                                fontWeight: 600,
                                                color: isLogout ? '#EB5757' : '#89652A'
                                            }
                                        }}
                                    />
                                    {!isLogout && (
                                        <ArrowForwardIcon
                                            fontSize="small"
                                            sx={{
                                                ml: 1,
                                                color: '#89652A',
                                                opacity: 0.5,
                                                fontSize: '18px'
                                            }}
                                        />
                                    )}
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </Box>
        </Menu>
    );

    const guestMenu = (
        <Menu
            anchorEl={guestAnchorEl}
            open={Boolean(guestAnchorEl)}
            onClose={handleGuestMenuClose}
            TransitionComponent={Fade}
            transitionDuration={300}
            PaperProps={{
                elevation: 0,
                sx: {
                    width: 240,
                    borderRadius: '16px',
                    mt: 1,
                    overflow: 'visible',
                    boxShadow: '0 2px 12px rgba(184, 134, 11, 0.10)',
                    '&:before': {
                        content: '""',
                        display: 'block',
                        position: 'absolute',
                        top: 0,
                        right: 20,
                        width: 10,
                        height: 10,
                        bgcolor: '#FFFFFF',
                        transform: 'translateY(-50%) rotate(45deg)',
                        zIndex: 0,
                    },
                },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
            <Box sx={{
                p: 1.2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                borderBottom: '1px solid rgba(184, 134, 11, 0.08)',
                borderRadius: '16px 16px 0 0',
            }}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: '#B8860B', mb: 0.5 }}>
         
                </Avatar>
                <Typography sx={{ fontSize: '18px', fontFamily: 'League Spartan', fontWeight: 700, color: '#B8860B', mb: 0.2 }}>
                    Welcome!
                </Typography>
                <Typography sx={{ fontSize: '13px', fontFamily: 'League Spartan', fontWeight: 500, color: '#89652A', opacity: 0.8 }}>
                    Please login or signup
                </Typography>
            </Box>
            <List sx={{ py: 0.5, px: 1 }}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                        onClick={() => handleGuestMenuClick('/signin')}
                        sx={{
                            py: 0.7,
                            px: 2,
                            borderRadius: '10px',
                            mb: 0.2,
                            backgroundColor: 'rgba(184,134,11,0.04)',
                            '&:hover': {
                                backgroundColor: 'rgba(184,134,11,0.12)',
                            },
                        }}
                    >
                        <ListItemText primary="Login" sx={{ '& .MuiTypography-root': { fontSize: '16px', fontFamily: "League Spartan", fontWeight: 600, color: '#89652A' } }} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton
                        onClick={() => handleGuestMenuClick('/choose')}
                        sx={{
                            py: 0.7,
                            px: 2,
                            borderRadius: '10px',
                            backgroundColor: 'rgba(184,134,11,0.04)',
                            '&:hover': {
                                backgroundColor: 'rgba(184,134,11,0.12)',
                            },
                        }}
                    >
                        <ListItemText primary="Signup" sx={{ '& .MuiTypography-root': { fontSize: '16px', fontFamily: "League Spartan", fontWeight: 600, color: '#89652A' } }} />
                    </ListItemButton>
                </ListItem>
            </List>
        </Menu>
    );

    return (
        <>
            <AppBar position="static" sx={{ backgroundColor: 'white', boxShadow: 'none', py: { xs: 1, md: 1 }, px: { xs: 0, sm: 1, md: 1, lg: 3.5 } }}>
                <Toolbar sx={{ justifyContent: 'space-between', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    <Box sx={{ ml: { xs: 1, md: 1 } }}>
                        <Link to="/">
                            <img src={Logo} alt="logo" style={{ maxWidth: '100%', height: 'auto' }} />
                        </Link>
                    </Box>
                    {!isMobile && (
                        <Box sx={{ display: 'flex', gap: { xs: 0, sm: 2, md: 1, lg: 4 }, alignItems: 'center', ml: { xs: 1, sm: 2, md: 6, lg: 10 } }}>
                            {navItems.map((item) => (
                                <Button
                                    key={item.label}
                                    onClick={() => handleNavClick(item.path)}
                                    sx={{
                                        color: location.pathname === item.path ? '#B8860B' : 'black',
                                        fontSize: '20px',
                                        fontFamily: "League Spartan",
                                        fontWeight: 500,
                                        textTransform: 'none',
                                        position: 'relative',
                                        '&:after': location.pathname === item.path ? {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: -5,
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            backgroundColor: '#B8860B'
                                        } : {},
                                    }}
                                >
                                    {item.label}
                                </Button>
                            ))}
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2, md: 2, lg: 3 }, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                        <Typography sx={{ fontSize: '18px', textTransform: 'capitalize', fontFamily: "League Spartan", fontWeight: 700, color: '#B8860B',marginTop:'5px' }}>
                            {(user as { firstName?: string })?.firstName}
                        </Typography>
                        {isAuthenticated && user ? (
                            <>
                                <IconButton
                                    onClick={handleMenuOpen}
                                    sx={{
                                        p: 0,
                                    }}
                                >
                                    <AccountCircle sx={{ fontSize: '42px', color: '#B8860B' }} />
                                </IconButton>
                                {userMenu}
                            </>
                        ) : (
                            <>
                                <IconButton onClick={handleGuestMenuOpen} sx={{ p: 0 }}>
                                    <img src={User} alt="user-icon" style={{ height: '42px', width: '42px' }} />
                                </IconButton>
                                {guestMenu}
                            </>
                        )}
                        {!isMobile && (
                            <Button
                                component={Link}
                                to="/contact-us"
                                sx={{
                                    backgroundColor: '#89652A',
                                    color: 'white',
                                    textTransform: 'none',
                                    fontSize: '20px',
                                    fontFamily: "League Spartan",
                                    fontWeight: 700,
                                    padding: "2px 25px",
                                    borderRadius: '20px',
                                }}
                            >
                                Contact us
                            </Button>
                        )}
                        {isMobile && (
                            <IconButton
                                onClick={handleDrawerToggle}
                                sx={{
                                    color: '#000',
                                    marginLeft: 1,
                                }}
                            >
                                <MenuRoundedIcon sx={{ fontSize: { xs: 30, md: 40 } }} />
                            </IconButton>
                        )}
                    </Box>
                </Toolbar>
            </AppBar>
            <Drawer variant="temporary" anchor="right" open={mobileOpen} onClose={handleDrawerToggle} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: '85vw', maxWidth: 340, overflowY: 'auto', backgroundColor: 'white' } }}>
                {drawer}
            </Drawer>
        </>
    );
};

export default NavigationBar;